import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  getSupportTicket, 
  addSupportMessage,
  uploadSupportFile,
  updateTicketStatus 
} from '../../services/api';
import { useSimpleWebSocket } from '../../hooks/useSimpleWebSocket';
import Loading from '../../components/common/Loading';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';

// Import modular components
import TicketHeader from '../../components/support/ticket/TicketHeader';
import MessagesList from '../../components/support/ticket/MessagesList';
import FileUploader from '../../components/support/ticket/FileUploader';
import MessageInput from '../../components/support/ticket/MessageInput';

import { 
  ArrowLeft, RefreshCw, AlertCircle, Lock, Send,
  Paperclip, X, Loader, Check, ExternalLink,
  MessageCircle, Calendar, FileText, Shield as ShieldIcon,
  Receipt, Menu, ChevronRight, HelpCircle, Mail,
  Clock, MoreVertical
} from 'lucide-react';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const { isConnected, subscribeToSupportTicket } = useSimpleWebSocket();
  const [realTimeMessages, setRealTimeMessages] = useState([]);
  const [isMobile, setIsMobile] = useState(false);


  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (id && user) {
      fetchTicket();
    } else if (!user) {
      navigate('/login');
    }
  }, [id, user, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);


  useEffect(() => {
    if (!id || !user?.user_id || !isConnected) return;
    
    // console.log(`✅ [TicketDetail] Subscribing to support ticket ${id}`);
    
    const unsubscribe = subscribeToSupportTicket(id, (newMessage) => {
      // console.log('🎯 [TicketDetail] WebSocket message received:', newMessage);
      
      setRealTimeMessages(prev => {
        // Check if this replaces a temp message
        const tempIndex = prev.findIndex(msg => 
          msg.isTemp && 
          msg.sender_id === newMessage.sender_id &&
          msg.message === newMessage.message
        );
        
        if (tempIndex !== -1) {
          // Replace temp message
          const updated = [...prev];
          updated[tempIndex] = { ...newMessage, isTemp: false };
          return updated;
        }
        
        // Check if message already exists
        const exists = prev.some(msg => 
          msg.message_id === newMessage.message_id
        );
        
        if (exists) return prev;
        
        // Add new message
        return [...prev, newMessage];
      });
      
      
    });
    
    return unsubscribe;
  }, [isConnected, id, user?.user_id, subscribeToSupportTicket]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const allMessages = React.useMemo(() => {
    if (!ticket?.messages) return realTimeMessages;
    
    // Combine arrays
    const combined = [...ticket.messages, ...realTimeMessages];
    
    // Remove duplicates - prefer real-time messages (they have latest data)
    const seen = new Set();
    const uniqueMessages = [];
    
    // Process in reverse to keep newest duplicates
    for (let i = combined.length - 1; i >= 0; i--) {
      const msg = combined[i];
      const key = msg.message_id || msg.temp_id || JSON.stringify(msg);
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueMessages.unshift(msg); // Add to beginning to maintain order
      }
    }
    
    // Sort by date
    return uniqueMessages.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date();
      const dateB = b.created_at ? new Date(b.created_at) : new Date();
      return dateA - dateB;
    });
  }, [ticket?.messages, realTimeMessages]);

  const fetchTicket = async (isRefreshing = false) => {
    try {
      if (!isRefreshing) setLoading(true);
      
      const response = await getSupportTicket(id);
      
      if (response.success && response.data) {
        setTicket(response.data);
      } else {
        throw new Error(response.error || 'Ticket not found');
      }
    } catch (error) {
      // console.error('Error fetching ticket:', error);
      alert('Failed to load ticket details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchTicket(true);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.slice(0, 5 - selectedFiles.length).map((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
      
      return {
        file: file,
        fileName: file.name,
        type: isImage ? 'image' : 'file',
        size: file.size,
        mimeType: file.type,
        isUploading: false,
        uploadProgress: 0,
        uploadError: null,
        uploadSuccess: false,
        previewUrl: isImage ? URL.createObjectURL(file) : null,
      };
    });
    
    setSelectedFiles([...selectedFiles, ...newFiles]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    const file = selectedFiles[index];
    // Revoke object URL if it's an image preview
    if (file.previewUrl) {
      URL.revokeObjectURL(file.previewUrl);
    }
    
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    // Revoke all object URLs
    selectedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    setSelectedFiles([]);
  };

  const uploadFile = async (file, index) => {
    try {
      // Update file state to show uploading
      const updatedFiles = [...selectedFiles];
      updatedFiles[index] = {
        ...updatedFiles[index],
        isUploading: true,
        uploadProgress: 0,
        uploadError: null
      };
      setSelectedFiles(updatedFiles);

      // Create FormData
      const formData = new FormData();
      formData.append('file', file.file);
      formData.append('ticket_id', id);
      formData.append('user_id', user.user_id.toString());
      
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setSelectedFiles(prev => {
          const newFiles = [...prev];
          if (newFiles[index]) {
            newFiles[index] = {
              ...newFiles[index],
              uploadProgress: Math.min(newFiles[index].uploadProgress + 20, 90)
            };
          }
          return newFiles;
        });
      }, 300);

      // Upload file
      const uploadResult = await uploadSupportFile(formData);
      
      clearInterval(progressInterval);
      
      if (uploadResult.success) {
        updatedFiles[index] = {
          ...updatedFiles[index],
          isUploading: false,
          uploadProgress: 100,
          uploadSuccess: true,
          attachmentId: uploadResult.attachment_id,
          fileUrl: uploadResult.file_url
        };
        setSelectedFiles(updatedFiles);
        
        return { success: true, data: uploadResult };
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
      
    } catch (error) {
      // console.error(`Upload failed for ${file.fileName}:`, error);
      
      const updatedFiles = [...selectedFiles];
      updatedFiles[index] = {
        ...updatedFiles[index],
        isUploading: false,
        uploadError: error.message,
        uploadProgress: 0
      };
      setSelectedFiles(updatedFiles);
      
      return { success: false, error: error.message };
    }
  };

  const uploadAllFiles = async () => {
    if (selectedFiles.length === 0) return [];

    setUploadingFiles(true);
    const uploadedFiles = [];
    const failedUploads = [];
    
    try {
      // Upload files sequentially
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        try {
          const result = await uploadFile(file, i);
          
          if (result.success) {
            uploadedFiles.push(result.data);
          } else {
            failedUploads.push({
              fileName: file.fileName,
              error: result.error
            });
          }
          
          // Small delay between uploads
          if (i < selectedFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
        } catch (error) {
          // console.error(`Error in upload ${i + 1}:`, error);
          failedUploads.push({
            fileName: file.fileName,
            error: error.message
          });
        }
      }
      
      // Show summary if any uploads failed
      if (failedUploads.length > 0) {
        alert(`${uploadedFiles.length} file(s) uploaded, ${failedUploads.length} failed.`);
      }
      
      return uploadedFiles;
      
    } catch (error) {
      // console.error('Error uploading files:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;
  
    // Create unique temp ID for this message
    const tempId = Date.now() + Math.random();
    
    try {
      setSendingMessage(true);
  
      const tempMessage = {
        temp_id: tempId,
        ticket_id: parseInt(id),
        sender_id: user.user_id,
        sender_type: 'user',
        message: newMessage.trim(),
        created_at: new Date().toISOString(),
        isTemp: true
      };
      
      // Show immediately
      setRealTimeMessages(prev => [...prev, tempMessage]);
  
      let uploadedFiles = [];
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        uploadedFiles = await uploadAllFiles();
      }
      
      let messageToSend = newMessage.trim();
      
      // If we only have files and no text, update the message
      if (!messageToSend && uploadedFiles.length > 0) {
        messageToSend = `Sent ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}`;
        // Update the temp message text
        setRealTimeMessages(prev => 
          prev.map(msg => 
            msg.temp_id === tempId 
              ? { ...msg, message: messageToSend }
              : msg
          )
        );
      }
      
      // Send to server
      if (messageToSend) {
        const messageData = {
          ticket_id: id,
          sender_id: user.user_id,
          message: messageToSend,
          message_type: 'text',
        };
  
        const response = await addSupportMessage(id, messageData);
        
        if (!response.success) {
          throw new Error(response.error || 'Failed to send message');
        }
      }
      
      // Clear inputs
      setNewMessage('');
      setSelectedFiles([]);
      
    } catch (error) {
      // console.error('Error sending message:', error);
      alert(error.message || 'Failed to send message');
      
      // Remove temp message on error
      setRealTimeMessages(prev => 
        prev.filter(msg => !msg.isTemp || msg.temp_id !== tempId)
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSendFilesOnly = async () => {
    if (selectedFiles.length === 0) return;

    // Create unique temp ID
    const tempId = Date.now() + Math.random();
  
    try {
      setSendingMessage(true);
      
      const tempMessage = {
        temp_id: tempId,
        ticket_id: parseInt(id),
        sender_id: user.user_id,
        sender_type: 'user',
        message: `Uploading ${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''}...`,
        created_at: new Date().toISOString(),
        isTemp: true
      };
      
      // Show immediately
      setRealTimeMessages(prev => [...prev, tempMessage]);
      
      // Upload files
      const uploadedFiles = await uploadAllFiles();
      
      if (uploadedFiles.length > 0) {
        // Update temp message
        setRealTimeMessages(prev => 
          prev.map(msg => 
            msg.temp_id === tempId 
              ? { ...msg, message: `Attached ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}` }
              : msg
          )
        );
        
        // Send to server
        const messageData = {
          ticket_id: id,
          sender_id: user.user_id,
          message: `Attached ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}`,
          message_type: 'text',
        };
  
        await addSupportMessage(id, messageData);
      }
      
      // Clear files
      setSelectedFiles([]);
      
      // ❌ REMOVED: fetchTicket(); // No refresh needed
      
    } catch (error) {
      // console.error('Error sending files:', error);
      alert(error.message || 'Failed to send files');
      
      // Remove temp message on error
      setRealTimeMessages(prev => 
        prev.filter(msg => msg.temp_id !== tempId)
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      setActionLoading(true);
      
      const response = await updateTicketStatus(id, {
        user_id: user.user_id,
        status: newStatus
      });
      
      if (response.success) {
        alert(`Ticket marked as ${newStatus.replace('_', ' ')}`);
        fetchTicket();
        setShowStatusModal(false);
      } else {
        throw new Error(response.error || 'Failed to update status');
      }
    } catch (error) {
      // console.error('Error updating status:', error);
      alert(error.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBack = () => {
    if (newMessage.trim() || selectedFiles.length > 0) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleDisputePress = (disputeId) => {
    navigate(`/disputes/${disputeId}`);
  };

  const handleOrderPress = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const openStatusModal = (status) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading text="Loading ticket details..." />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Ticket not found</h2>
          <p className="text-gray-600 mb-4">The requested support ticket could not be loaded.</p>
          <button
            onClick={() => navigate('/support')}
            className="px-6 py-2 bg-[#ff660e] text-white rounded-lg hover:bg-[#ff5500] transition"
          >
            Back to Support
          </button>
        </div>
      </div>
    );
  }

  const canReply = ticket.status === 'open' || ticket.status === 'in_progress' || ticket.status === 'awaiting_user';
  const isResolvedOrClosed = ticket.status === 'resolved' || ticket.status === 'closed';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-white shadow-sm sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5 text-black" />
            </button>
            
            <div className="text-center">
              <h1 className="text-sm font-bold text-black truncate max-w-[150px] mx-auto">
                {ticket.title}
              </h1>
              <p className="text-xs text-gray-500">Ticket #{ticket.ticket_id}</p>
            </div>
            
            <button
              onClick={onRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <div className="hidden lg:block">
        <Header />
      </div>

      <main className="max-w-7xl mx-auto">
        {/* Desktop Breadcrumb */}
        <div className="hidden lg:block px-6 pt-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={() => navigate('/support')}
              className="hover:text-black"
            >
              Support
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-black font-medium">Ticket #{ticket.ticket_id}</span>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-4 lg:gap-6 p-4 lg:p-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-4 lg:space-y-6">
            {/* Desktop Header Actions */}
            <div className="hidden lg:flex items-center justify-between mb-2">
              <div>
                <h1 className="text-2xl font-bold text-black">{ticket.title}</h1>
                <p className="text-gray-600 mt-1">Ticket #{ticket.ticket_id} • Support Center</p>
              </div>
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Ticket Header - Mobile */}
            <div className="lg:hidden bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="font-bold text-black text-base">{ticket.title}</h2>
                  <p className="text-xs text-gray-500 mt-1">Ticket #{ticket.ticket_id}</p>
                </div>
                <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  ticket.status === 'open' ? 'bg-orange-50 text-orange-700' :
                  ticket.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                  ticket.status === 'awaiting_user' ? 'bg-purple-50 text-purple-700' :
                  ticket.status === 'resolved' ? 'bg-green-50 text-green-700' :
                  'bg-gray-50 text-gray-700'
                }`}>
                  {ticket.status.replace('_', ' ')}
                </div>
              </div>

              {/* Priority and Category */}
              <div className="flex flex-wrap gap-2 mb-3">
                <div className="px-3 py-1 bg-gray-100 rounded-lg">
                  <span className="text-xs font-medium text-gray-600">
                    {ticket.priority} Priority
                  </span>
                </div>
                <div className="px-3 py-1 bg-gray-100 rounded-lg">
                  <span className="text-xs font-medium text-gray-600">
                    {ticket.category_name || 'General'}
                  </span>
                </div>
              </div>

              {/* Created date */}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>
                  Created {new Date(ticket.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>

            {/* Ticket Header - Desktop */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-black mb-2">{ticket.title}</h2>
                  <div className="flex items-center gap-3 text-gray-600">
                    <span>Ticket #{ticket.ticket_id}</span>
                    <span>•</span>
                    <span>{ticket.category_name || 'General Support'}</span>
                    <span>•</span>
                    <span className="font-medium">{ticket.priority} Priority</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={onRefresh}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </button>
                </div>
              </div>

              {/* Related Info - Desktop */}
              {(ticket.dispute_id || ticket.order_id) && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <h3 className="font-semibold text-black mb-3">Related Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {ticket.dispute_id && (
                      <button
                        onClick={() => handleDisputePress(ticket.dispute_id)}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-white"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-red-50 rounded-lg">
                            <ShieldIcon className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Dispute #{ticket.dispute_id}</p>
                            <p className="text-sm text-gray-500">View dispute details</p>
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400" />
                      </button>
                    )}
                    
                    {ticket.order_id && (
                      <button
                        onClick={() => handleOrderPress(ticket.order_id)}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-white"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-50 rounded-lg">
                            <Receipt className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">Order #{ticket.order_id}</p>
                            <p className="text-sm text-gray-500">View order details</p>
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Dispute/Order Info - Mobile */}
            {(ticket.dispute_id || ticket.order_id) && (
              <div className="lg:hidden bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-5 h-5 text-[#ff660e]" />
                  <h3 className="font-bold text-black">Related Information</h3>
                </div>
                
                <div className="space-y-2">
                  {ticket.dispute_id && (
                    <button
                      onClick={() => handleDisputePress(ticket.dispute_id)}
                      className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <ShieldIcon className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium">Dispute #{ticket.dispute_id}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                  
                  {ticket.order_id && (
                    <button
                      onClick={() => handleOrderPress(ticket.order_id)}
                      className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Order #{ticket.order_id}</span>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Messages Section */}
            <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm lg:shadow-lg overflow-hidden">
              <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 lg:w-6 lg:h-6 text-[#ff660e]" />
                    <h3 className="font-bold text-black text-base lg:text-lg">Conversation</h3>
                  </div>
                  <span className="text-sm text-gray-500">
                    {ticket.messages?.length || 0} messages
                  </span>
                </div>
              </div>
              
              <div className="p-4 lg:p-6 min-h-[300px] lg:min-h-[400px] max-h-[400px] lg:max-h-[500px] overflow-y-auto">
                <MessagesList
                  messages={allMessages}  // Use allMessages instead of ticket.messages
                  userId={user.user_id}
                />
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* File Uploader Section */}
            {selectedFiles.length > 0 && (
              <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm lg:shadow-lg p-4 lg:p-6">
                <FileUploader
                  selectedFiles={selectedFiles}
                  fileInputRef={fileInputRef}
                  onFileSelect={handleFileSelect}
                  onRemoveFile={removeFile}
                  onClearAll={clearAllFiles}
                  uploadingFiles={uploadingFiles}
                />
              </div>
            )}

            {/* Message Input Section */}
            <div className="bg-white rounded-xl lg:rounded-2xl shadow-sm lg:shadow-lg p-4 lg:p-6">
              <MessageInput
                ticket={ticket}
                canReply={canReply}
                isResolvedOrClosed={isResolvedOrClosed}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                selectedFiles={selectedFiles}
                sendingMessage={sendingMessage}
                uploadingFiles={uploadingFiles}
                onSendMessage={handleSendMessage}
                onSendFilesOnly={handleSendFilesOnly}
                onUpdateStatus={openStatusModal}
                actionLoading={actionLoading}
                fileInputRef={fileInputRef}
                onFileSelect={handleFileSelect}
              />
            </div>

            {/* Mobile Actions Footer */}
            <div className="lg:hidden bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-bold text-black mb-3">Ticket Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                {!isResolvedOrClosed && (
                  <>
                    <button
                      onClick={() => openStatusModal('resolved')}
                      disabled={actionLoading}
                      className="py-2.5 bg-green-50 text-green-700 rounded-lg font-medium text-sm hover:bg-green-100 disabled:opacity-50"
                    >
                      Mark Resolved
                    </button>
                    <button
                      onClick={() => openStatusModal('closed')}
                      disabled={actionLoading}
                      className="py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-200 disabled:opacity-50"
                    >
                      Close Ticket
                    </button>
                  </>
                )}
              </div>
            </div><br /><br /><br />
          </div>

          {/* Sidebar - Desktop Only */}
          <div className="hidden lg:block space-y-6">
            {/* Quick Actions Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-black mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {!isResolvedOrClosed && (
                  <>
                    <button
                      onClick={() => openStatusModal('resolved')}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 p-3 border border-green-200 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 disabled:opacity-50"
                    >
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Mark as Resolved</span>
                    </button>
                    
                    <button
                      onClick={() => openStatusModal('closed')}
                      disabled={actionLoading}
                      className="w-full flex items-center gap-3 p-3 border border-gray-300 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 disabled:opacity-50"
                    >
                      <X className="w-5 h-5" />
                      <span className="font-medium">Close Ticket</span>
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => navigate('/support')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="font-medium">Back to Support</span>
                </button>
                
                <button
                  onClick={onRefresh}
                  disabled={refreshing}
                  className="w-full flex items-center gap-3 p-3 border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="font-medium">Refresh Ticket</span>
                </button>
              </div>
            </div>

            {/* Ticket Status Info */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6">
              <h3 className="font-semibold text-black mb-4">Ticket Status</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    ticket.status === 'open' ? 'bg-orange-500' :
                    ticket.status === 'in_progress' ? 'bg-blue-500' :
                    ticket.status === 'awaiting_user' ? 'bg-purple-500' :
                    ticket.status === 'resolved' ? 'bg-green-500' :
                    'bg-gray-500'
                  }`}></div>
                  <div>
                    <p className="font-medium text-black">Current Status</p>
                    <p className="text-sm text-gray-600 capitalize">{ticket.status.replace('_', ' ')}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    canReply ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <div>
                    <p className="font-medium text-black">Can Reply</p>
                    <p className="text-sm text-gray-600">
                      {canReply ? 'Yes - You can reply to this ticket' : 'No - Ticket is closed/resolved'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                  <div>
                    <p className="font-medium text-black">Support Response Time</p>
                    <p className="text-sm text-gray-600">Typically within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Help Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-black mb-4">Need More Help?</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/support/new-ticket')}
                  className="w-full flex items-center gap-3 p-3 border border-gray-300 rounded-xl hover:bg-gray-50"
                >
                  <Send className="w-5 h-5" />
                  <span className="font-medium">Create New Ticket</span>
                </button>
                
                <a
                  href="mailto:support@ecomart.ng"
                  className="block w-full text-center py-2.5 text-sm border border-[#ff660e] text-[#ff660e] rounded-lg font-medium hover:bg-orange-50"
                >
                  Email Support Team
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Desktop Footer */}
      <div className="hidden lg:block">
        <Footer />
      </div>

      {/* Status Update Modal */}
      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={() => handleUpdateStatus(selectedStatus)}
        title="Update Ticket Status"
        message={`Are you sure you want to mark this ticket as "${selectedStatus?.replace('_', ' ') || ''}"?`}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmColor="#059669"
      />
    </div>
  );
};

export default TicketDetail;